import { Map } from "immutable";
import { any, async, AsyncState, Fun, httpResult, HttpResult, loadingAsyncState, nothing, unloadedAsyncState, Updater, Widget } from "widgets-for-react";

export type SingleValidatorResult = 
| { kind:"passed" }
| { kind:"validating" }
| { kind:"failed", message:string | JSX.Element } 
export type NestedValidatorResult = { kind:"nested", value:ValidatorResult }
export type ValidatorResult = 
  { 
    values:Map<string,SingleValidatorResult | NestedValidatorResult>
    path(...keys:Array<string>):SingleValidatorResult 
  }

const mapToValidatorResult = (map:Map<string,SingleValidatorResult | NestedValidatorResult>) : ValidatorResult => ({
  values:map, 
  path:(...keys:Array<string>) : SingleValidatorResult => {
    let currentMap = map
    for(let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const value = currentMap.get(key)
      if (!value) return ({ kind:"passed" })
      if (value.kind != "nested") return value
      currentMap = value.value.values
    }
    return ({ kind:"passed" })
  }
})

export const singleValidatorResultBuilders = {
  passed: (key:string) : SingleValidatorResult => ({ kind:"passed" }),
  validating: (key:string) : SingleValidatorResult => ({ kind:"validating" }),
  failed: (key:string,message:string | JSX.Element) : SingleValidatorResult => ({ kind:"failed", message:message })
}

export const validatorResultBuilders = {
  nested: (key:string, v:ValidatorResult) : ValidatorResult => mapToValidatorResult(Map<string,SingleValidatorResult | NestedValidatorResult>().set(key,{ kind:"nested", value:v })),
  passed: (key:string) : ValidatorResult => mapToValidatorResult(Map<string,SingleValidatorResult>().set(key,{ kind:"passed" })),
  validating: (key:string) : ValidatorResult => mapToValidatorResult(Map<string,SingleValidatorResult>().set(key,{ kind:"validating" })),
  failed: (key:string,message:string) : ValidatorResult => mapToValidatorResult(Map<string,SingleValidatorResult>().set(key, ({ kind:"failed", message:message })))
}

export const isValidatorPassed = (v:ValidatorResult) : boolean => 
  v.values.every(v => v.kind == "passed" || (v.kind == "nested" && isValidatorPassed(v.value)))
const mergeValidatorResults = (v1:ValidatorResult, v2:ValidatorResult) : ValidatorResult => {
  let res = v1.values
  v2.values.forEach((v,k) => {
    res = res.set(k,v)
  })
  return mapToValidatorResult(res)
}

export const getErrorMessage = (validatorResult: SingleValidatorResult) => validatorResult.kind === "failed" ? validatorResult.message : undefined

export type PromiseValidator<state,t> = (state:state,value:t) => Promise<boolean | undefined>


///////////////////////////// DEFINITIONS
export type ValidatorAlgebra<state,t> = {
  map<u>(transformValue:Fun<[state,t],u>,transformResult:Fun<ValidatorResult, ValidatorResult>) : Validator<state,u>
  and(_:Validator<state,t>) : Validator<state,t>
  or(_:Validator<state,t>) : Validator<state,t>
  // not() : Validator<state,t>
  widget() : Widget<Updater<Validator<state,t>>>
  evaluateResult() : ValidatorResult
  setValueToValidate(state:state, value:t) : Validator<state,t>
}

type YesValidator<state,t> = ValidatorAlgebra<state,t> & {
  kind:"yes",
  key:string
}

type MapValidator<state,t> = ValidatorAlgebra<state,t> & {
  kind:"map", 
  transformValue:Fun<[state,t],any>,
  transformResult:Fun<ValidatorResult, ValidatorResult>,
  underlyingValidator:Validator<state,any>
}

type FromPromiseValidator<state,t> = ValidatorAlgebra<state,t> & {
  kind:"fromPromise", 
  p:PromiseValidator<state,t>,
  currentValidation:AsyncState<boolean | undefined>
  nextValidation?:AsyncState<boolean | undefined>
  keySeed:number
  keyCounter:number,
  key:string,
  errorMessage:() => string
}

type AndValidator<state,t> = ValidatorAlgebra<state,t> & {
  kind:"and", l:Validator<state,t>, r:Validator<state,t>
}

type OrValidator<state,t> = ValidatorAlgebra<state,t> & {
  kind:"or", l:Validator<state,t>, r:Validator<state,t>
}

// type NotValidator<state,t> = ValidatorAlgebra<state,t> & {
//   kind:"not", l:Validator<state,t>
// }

export type Validator<state,t> = 
| YesValidator<state,t>
| FromPromiseValidator<state,t>
| AndValidator<state,t>
| OrValidator<state,t>
// | NotValidator<state,t>
| MapValidator<state,t>
//////////////////////////////////////////


///////////////////////////// CONSTRUCTORS
const algebra = <state,t>() : ValidatorAlgebra<state,t> => ({
  map:mapValidator as <u>(transformValue:Fun<[state,t],u>,transformResult:Fun<ValidatorResult, ValidatorResult>) => Validator<state,u>,
  and:andValidator,
  or:orValidator,
  // not:notValidator,
  evaluateResult:evaluateValidator,
  widget:widget,
  setValueToValidate:setValueToValidate
})

export const yesValidator = function<state,t>(key:string) : YesValidator<state,t> { return { kind:"yes", key:key, ...algebra() } }

// map<u>(f:Fun<[state,t],u>) : Validator<state,u>
const mapValidator = function<state,t>(this:Validator<state,any>, transformValue:Fun<[state,t],any>, transformResult:Fun<ValidatorResult, ValidatorResult>) : MapValidator<state,t> { return { kind:"map", transformValue:transformValue, transformResult:transformResult, underlyingValidator:this, ...algebra() } }

const andValidator = function<state,t>(this:Validator<state,t>, r:Validator<state,t>) : AndValidator<state,t> { return { kind:"and", l:this, r:r, ...algebra() } }

const orValidator = function<state,t>(this:Validator<state,t>, r:Validator<state,t>) : OrValidator<state,t> { return { kind:"or", l:this, r:r, ...algebra() } }

// const notValidator = function<state,t>(this:Validator<state,t>) : NotValidator<state,t> { return { kind:"not", l:this, ...algebra() } }

export const fromPromiseValidator = <state,t>(p:PromiseValidator<state,t>, key:string, errorMessage:() => string) : FromPromiseValidator<state,t> => 
  ({ kind:"fromPromise", key:key, p:p, errorMessage:errorMessage, currentValidation:unloadedAsyncState(), nextValidation:undefined, keySeed:Math.random(), keyCounter:0, ...algebra() })
//////////////////////////////////////////


///////////////////////////// EVALUATION/EXECUTION
const evaluateValidator = function<state,t>(this:Validator<state,t>) : ValidatorResult { 
  if (this.kind == "fromPromise") {
    if (this.currentValidation.kind == "loaded") {
      if (this.currentValidation.value == undefined) return validatorResultBuilders.validating(this.key)
      if (this.currentValidation.value == true) return validatorResultBuilders.passed(this.key)
      return validatorResultBuilders.failed(this.key, this.errorMessage())
    }
    return validatorResultBuilders.validating(this.key)
  } else if (this.kind == "and") {
    const evalLeft = this.l.evaluateResult()
    const evalRight = this.r.evaluateResult()
    return mergeValidatorResults(evalLeft, evalRight)
  } else if (this.kind == "or") {
    const evalLeft = this.l.evaluateResult()
    if (isValidatorPassed(evalLeft)) return evalLeft
    return this.r.evaluateResult()
  // } else if (this.kind == "not") {
  //   const evalLeft = this.l.evaluateResult()
  //   if (isPassed(evalLeft) == "passed") return failed("")
    return evalLeft
  } else if (this.kind == "map") {
    return this.transformResult(this.underlyingValidator.evaluateResult())
  } else {
    return validatorResultBuilders.passed(this.key)
  }
}

const setValueToValidate = function<state,t>(this:Validator<state,t>, state:state, value:t) : Validator<state,t> { 
  if (this.kind == "fromPromise") {
    const self = this
    const p = this.p
    const asyncSuccess : Fun<boolean | undefined, HttpResult<boolean | undefined>> = value => httpResult.success(value)
    if (this.currentValidation.kind != "loading") return {...this, currentValidation:loadingAsyncState(() => p(state,value).then(asyncSuccess)), nextValidation:undefined, keyCounter:this.keyCounter+1 }
    else return {...this, nextValidation:loadingAsyncState(() => p(state,value).then(asyncSuccess) )}
  } else if (this.kind == "and") {
    return {...this, l:this.l.setValueToValidate(state,value), r:this.r.setValueToValidate(state,value)}
  } else if (this.kind == "or") {
    return {...this, l:this.l.setValueToValidate(state,value), r:this.r.setValueToValidate(state,value)}
  // } else if (this.kind == "not") {
  //   return {...this, l:this.l.setValueToValidate(state,value) }
  } else if (this.kind == "map") {
    return {...this, underlyingValidator:this.underlyingValidator.setValueToValidate(state, this.transformValue([state,value])) }
  } else {
    return this
  }
}

const widget = function<state,t>(this:Validator<state,t>) : Widget<Updater<Validator<state,t>>> { 
  if (this.kind == "fromPromise") {
    const self = this
    return async<boolean | undefined>(this.currentValidation, undefined, { key:`async-validator-${self.keySeed}/${self.keyCounter}` }).map(u => {
      const newCurrentValidation = u(self.currentValidation)
      const nextValidation = self.nextValidation
      if (newCurrentValidation.kind != "loading" && nextValidation != undefined) {
        return (v) : Validator<state,t> => v.kind === "fromPromise" ? ({...v, currentValidation:nextValidation, nextValidation:undefined, keyCounter:self.keyCounter+1 }) : v
      } else {
        return (v) : Validator<state,t> => v.kind === "fromPromise" ? ({...v, currentValidation:newCurrentValidation}) : v
      }
    })
  } else if (this.kind == "or" || this.kind == "and") {
    return any([
      this.l.widget().map(lupdater => (v) : Validator<state,t> => v.kind === "or" || v.kind === "and" ? ({...v, l:lupdater(v.l)}) : v),
      this.r.widget().map(rupdater => (v) : Validator<state,t> => v.kind === "or" || v.kind === "and" ? ({...v, r:rupdater(v.r)}) : v),
    ])
  // } else if (this.kind == "not") {
  //   const self = this
  //   return this.l.widget().map<Validator<state,t>>(l => ({...self, l:l}))
  } else if (this.kind == "map") {
    return this.underlyingValidator.widget().map(underlyingUpdater => (v) :  Validator<state,t> => v.kind === "map" ? ({...v, underlyingValidator:underlyingUpdater(v.underlyingValidator)}): v)
  } else {
    return nothing()
  }
}
//////////////////////////////////////////
